import { useThemeStore } from "@/shared/stores/theme.store";
import { HStack, Icon, VStack } from "@/shared/ui/primitives";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Footer = () => {
	const { t, i18n } = useTranslation();
	const { theme, setTheme } = useThemeStore();
	const navigate = useNavigate();

	const navigateWithScrollTop = (to: "/company" | "/pricing") => {
		const scrollRoot = document.getElementById("root");
		if (scrollRoot) {
			scrollRoot.scrollTo({ top: 0, left: 0, behavior: "auto" });
		} else {
			window.scrollTo({ top: 0, left: 0, behavior: "auto" });
		}
		navigate({ to });
	};

	const langToggle = (
		<button onClick={() => i18n.changeLanguage(i18n.language === "ko" ? "en" : "ko")}>
			{i18n.language === "ko" ? t("footer.lang.ko") : t("footer.lang.en")}
		</button>
	);

	const themeToggle = (
		<button onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
			<Icon
				name={theme === "dark" ? "Dark" : theme === "system" ? "Robot" : "Light"}
				width={20}
				height={20}
			/>
		</button>
	);

	const termsLinks = (
		<VStack className="gap-[10px] font-normal text-ct-weak items-start">
			<button onClick={() => window.open("/legal/terms", "_blank")}>{t("sitemap.terms")}</button>
			<button onClick={() => window.open("/legal/privacy", "_blank")}>{t("sitemap.privacy")}</button>
			<button onClick={() => window.open("/legal/refund", "_blank")}>{t("sitemap.refund")}</button>
		</VStack>
	);

	const contactInfo = (
		<VStack className="w-full gap-[10px] items-start">
			<button onClick={() => window.open("mailto:inflectco@gmail.com", "_blank")}>
				<HStack className="gap-[10px] items-start">
					<Icon name="Mail" width={20} height={20} className="shrink-0" />
					<span>inflectco@gmail.com</span>
				</HStack>
			</button>
			<HStack className="gap-[10px] items-start">
				<Icon name="Location" width={20} height={20} className="shrink-0" />
				<span>{t("business.location")}</span>
			</HStack>
			<button onClick={() => window.open("https://www.instagram.com/inflect.kr", "_blank")}>
				<HStack className="gap-[10px] items-start">
					<Icon name="Instagram" width={20} height={20} className="shrink-0" />
					<span>inflect.kr</span>
				</HStack>
			</button>
		</VStack>
	);

	const businessInfo = (
		<VStack className="w-full items-start gap-[10px]">
			<HStack className="gap-[10px]">
				<span>{t("business.companyName")}</span>
				<span>인플렉트</span>
			</HStack>
			<HStack className="gap-[10px]">
				<span>{t("business.representative")}</span>
				<span>민경민, 정지유</span>
			</HStack>
			<HStack className="gap-[10px]">
				<span>{t("business.businessRegistrationNumber")}</span>
				<span>544-10-03428</span>
			</HStack>
			<HStack className="gap-[10px]">
				<span>{t("business.phone")}</span>
				<span>010-9264-0797</span>
			</HStack>
		</VStack>
	);

	const sitemapLinks = (
		<VStack className="w-full gap-[10px] items-start font-normal">
			<button onClick={() => navigateWithScrollTop("/company")}>{t("sitemap.company")}</button>
			<button onClick={() => navigateWithScrollTop("/pricing")}>{t("sitemap.pricing")}</button>
			<button onClick={() => navigate({ to: "/login" })}>{t("sitemap.login")}</button>
			<button onClick={() => navigate({ to: "/workflow" })}>{t("sitemap.workflow")}</button>
		</VStack>
	);

	return (
		<footer className="w-full bg-bg-regular text-sm text-ct-regular font-medium">
			{/* 데스크톱 */}
			<HStack className="hidden md:flex w-full min-h-[300px] px-[60px] py-[40px] items-stretch gap-[60px]">
				<VStack className="gap-[20px] p-[6px] shrink-0">
					{contactInfo}
					{businessInfo}
				</VStack>
				<VStack className="p-[6px] items-start justify-between flex-1">
					{sitemapLinks}
					{termsLinks}
				</VStack>
				<VStack className="justify-between items-end shrink-0">
					<HStack className="gap-[16px] p-[4px] text-ct-weak">
						{langToggle}
						{themeToggle}
					</HStack>
					<Icon name="Logo" width={150} height={40} viewBox="0 0 180 48" />
				</VStack>
			</HStack>

			{/* 모바일 */}
			<VStack className="md:hidden w-full px-[20px] py-[40px] gap-[32px] items-start">
				<VStack className="w-full gap-[28px] items-start">
					{contactInfo}
					{businessInfo}
					{sitemapLinks}
				</VStack>
				<div className="w-full h-px bg-ct-weak/10" />
				<VStack className="w-full gap-[12px] items-start">
					{termsLinks}
				</VStack>
				<HStack className="w-full justify-between items-center">
					<HStack className="gap-[16px] text-ct-weak">
						{langToggle}
						{themeToggle}
					</HStack>
					<Icon name="Logo" width={100} height={28} viewBox="0 0 180 48" />
				</HStack>
			</VStack>
		</footer>
	);
};
